import type { TMSService } from '~/types/msService/models'
import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'

const mn = '[cynqueModules/dataObjects/MSServiceJustAddLabel.ts]'

function _logPreprocessing(message: string, arg?: any) {
  if (getPublicRuntimeConfig().doSuppressPreprocessingMSSDataBeforeRender) {
    return
  }
  console.debug(message, arg)
}

/**
 * The one value that tells otherwise-identical msServices apart.
 *
 * WHICH value depends on the product: `keyIdentifierFieldName` names a key
 * inside the msService's own `fieldSetData` JSON -- `phoneNumber` for POTS,
 * `circuitId` for a circuit, `Model` for hardware. Most products name none, and
 * get nothing.
 *
 * Prefer-then-derive, because the two backends differ: the API sends
 * `key_identifier_value` on the msService itself, and the mock does not, so
 * deriving is what makes the picker work under MSW. (MSService() in
 * ./MSService.ts derives unconditionally for the msServices cards' "Key
 * Identifier" column -- left alone on purpose, since changing which of the two
 * sources wins on a card is a different question from labelling a select.)
 */
function _keyIdentifierValue(msService: TMSService) {
  const fn = `${mn} _keyIdentifierValue |`
  if (msService.keyIdentifierValue) {
    return msService.keyIdentifierValue
  }
  const fieldName = msService.product?.keyIdentifierFieldName
  if (!fieldName || !msService.fieldSetData) {
    return undefined
  }
  try {
    return JSON.parse(msService.fieldSetData)[fieldName]
  } catch (e) {
    console.error(
      `${fn} couldn't parse fieldSetData:`,
      msService.fieldSetData,
      e
    )
    return undefined
  }
}

// Data Object

/**
 * Label an msService for a select list: "<product> - <location>", plus the key
 * identifier when the product has one.
 *
 * ⚠ The two-part stem is needed BECAUSE the mock is narrower than the server.
 * The server's company_ms_services rows carry a real `label` column, written by
 * a trigger as `product.label || ' - ' || location.label`; the mock's rows have
 * no `label` at all. A select list built on `label` alone would work against the
 * API and come up empty under MSW.
 *
 * ⚠ And the stem alone is NOT enough to choose by. Five POTS lines at one
 * location share a product, a location, a supplier, a contract and a status --
 * in the seed data, Metropolis Bank's POTS group offers exactly that -- so the
 * stem yields five identical options and the picker cannot be used correctly.
 * The key identifier is what the app itself uses to separate them: it is a
 * column of its own on every msServices card. Same reasoning as the `contact`
 * field's 'namePlusBiz' in the Case spec.
 *
 * ⚠ ⚠ The label a doReturnObject select SENDS is not the label that comes BACK.
 * The API stores only the FK and re-derives the two-part label from its own
 * columns, so a case saved in production reads back "POTS - Boston" however this
 * one is spelled. The mock stores what it is given, so a case created under MSW
 * keeps the longer form -- a mock-only difference, and the same one every
 * 'labelWithHrid' / 'namePlusBiz' picker in the app already has.
 *
 * It used to append ` - <company>` instead, back when its one caller read
 * `/api/companymsservices` whole and offered every msService in the system.
 * Its caller is now scoped to one service group of one company
 * (getCompanyMSServicesForCompanyMasterServiceId), so the company is already
 * fixed by the two selects above the list.
 */
export function MSServiceJustAddLabel(msService: TMSService) {
  const fn = `[${mn} MSServiceJustAddLabel |`
  _logPreprocessing(`${fn} Preprocessing msService's data before render`)
  const keyIdentifierValue = _keyIdentifierValue(msService)
  const stem = `${msService.product.descr} - ${msService.location.label}`
  msService.label = keyIdentifierValue
    ? `${stem} | ${keyIdentifierValue}`
    : stem
  _logPreprocessing('', msService)

  return msService
}
